/**
 * `payroll.linkSelfAsEmployee` — the admin's invite-less route into "My pay".
 *
 * A company admin who is also on their own payroll does not need to email
 * themselves an ESS invite and accept it: the server links their own user
 * account to the employee row carrying their own authenticated email (see that
 * procedure's docblock for what authorises it). This hook is the call site.
 *
 * When no row carries their email the server refuses with
 * `PAYROLL_SELF_LINK_PROFILE_REQUIRED` (classified here as `profile_required`):
 * a record cannot be created from schema defaults, so the caller collects pay
 * details and calls `link_self(profile)` again. `profile` is ignored
 * server-side when a matching row already exists.
 *
 * Never throws — the caller renders the classified outcome inline, matching
 * `use_employee_mutations.ts`'s contract.
 */
import {
  getTrpcClient,
  isBadRequestTrpcError,
  isPlanPayrollEmployeeCapExceededError,
  isSelfLinkProfileRequiredError,
} from '../lib/trpc';
import { useOAuthEntityContext } from '../lib/auth/oauth_entity_context';
import type { SelfLinkProfileInput } from '../screens/my_pay/my_pay_self_link_form';

/** Why a self-link attempt did not produce a usable link. */
export type SelfLinkFailure =
  /** No employee row carries the caller's email, and no profile was sent. */
  | 'profile_required'
  /** Creating the record would exceed the plan's employee allowance. */
  | 'plan_cap'
  /** The server rejected the supplied profile (`message` says why). */
  | 'validation'
  /** No employee row in this company carries the caller's email address. */
  | 'no_matching_employee'
  /** The matching row is already linked to a DIFFERENT user account. */
  | 'already_linked_elsewhere'
  /** No resolved entity grant yet. */
  | 'no_entity'
  | 'error';

export type SelfLinkResult =
  | { outcome: 'success'; employee_name: string }
  | { outcome: 'failure'; failure: SelfLinkFailure; message?: string };

const NO_MATCH_MESSAGE =
  'No employee record in this company matches your email address';
const ALREADY_LINKED_MESSAGE = 'Employee is already linked to a user account';

/**
 * Classify a thrown error. Order matters: the machine-readable ledgerCodes
 * come first (the profile-required error carries the same text as the legacy
 * no-match error, so it must be recognised by code before the message
 * fallback), then the pinned messages, then any other BAD_REQUEST.
 */
export function classifySelfLinkError(
  err: unknown,
): Extract<SelfLinkResult, { outcome: 'failure' }> {
  const message = err instanceof Error ? err.message : '';
  if (isSelfLinkProfileRequiredError(err)) {
    return { outcome: 'failure', failure: 'profile_required' };
  }
  if (isPlanPayrollEmployeeCapExceededError(err)) {
    return { outcome: 'failure', failure: 'plan_cap', message };
  }
  if (message === ALREADY_LINKED_MESSAGE) {
    return { outcome: 'failure', failure: 'already_linked_elsewhere' };
  }
  if (message === NO_MATCH_MESSAGE) {
    return { outcome: 'failure', failure: 'no_matching_employee' };
  }
  if (isBadRequestTrpcError(err)) {
    return { outcome: 'failure', failure: 'validation', message };
  }
  return { outcome: 'failure', failure: 'error' };
}

/** The write path, injectable so the forwarding/classification is testable without a tRPC client. */
export interface SelfLinkDeps {
  mutate: (input: {
    firm_id: string;
    entity_id: string;
    profile?: SelfLinkProfileInput;
  }) => Promise<{ employee_name: string }>;
}

/**
 * Run one self-link attempt. `profile` is forwarded only when supplied (the
 * key is omitted otherwise, so the first attempt is byte-identical to the
 * pre-profile call). Never throws.
 */
export async function performSelfLink(
  deps: SelfLinkDeps,
  ids: { firm_id: string; entity_id: string },
  profile?: SelfLinkProfileInput,
): Promise<SelfLinkResult> {
  try {
    const res = await deps.mutate({
      ...ids,
      ...(profile === undefined ? {} : { profile }),
    });
    return { outcome: 'success', employee_name: res.employee_name };
  } catch (err) {
    console.error('[wagepro-web] linkSelfAsEmployee failed:', err);
    return classifySelfLinkError(err);
  }
}

export interface UseSelfLinkResult {
  /** Absent until an entity grant resolves. */
  link_self:
    | ((profile?: SelfLinkProfileInput) => Promise<SelfLinkResult>)
    | undefined;
}

export function useSelfLink(): UseSelfLinkResult {
  const entity = useOAuthEntityContext();
  if (entity.status !== 'ready') return { link_self: undefined };

  const { firm_id, entity_id } = entity;
  return {
    link_self: (profile?: SelfLinkProfileInput): Promise<SelfLinkResult> =>
      performSelfLink(
        {
          mutate: (input) =>
            getTrpcClient().payroll.linkSelfAsEmployee.mutate(input),
        },
        { firm_id, entity_id },
        profile,
      ),
  };
}
